/**
 * Batch Job Detail Page
 * Full details for a single batch job execution
 */

import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, AlertCircle, BarChart2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LoadingState, ErrorState } from '@/components/ui/loading-state'
import { formatDate, formatDuration, formatNumber } from '@/lib/utils'
import { getBatchJobExecution } from '../queries/batchJobs'
import { JobStatusBadge } from '../components/JobStatusBadge'
import { TriggerTypeBadge } from '../components/TriggerTypeBadge'
import type { BatchJobExecution } from '../types'

function formatSchedule(cron: string | null | undefined): string {
  if (!cron) return 'Manual only'
  const map: Record<string, string> = {
    '0 0 */2 * * *': 'Every 2 hours',
    '0 0 2 * * *': 'Daily 2:00 AM UTC',
    '0 0 5 * * *': 'Daily 5:00 AM UTC',
    '0 */30 * * * *': 'Every 30 min',
  }
  return map[cron] ?? cron
}

interface InfoRowProps {
  label: string
  value: React.ReactNode
}

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 py-2.5 border-b border-gray-50 last:border-0">
      <span className="w-40 flex-shrink-0 text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value}</span>
    </div>
  )
}

interface MetricRowProps {
  label: string
  value: number | null | undefined
  accent?: 'green' | 'red' | 'gray'
}

function MetricRow({ label, value, accent = 'gray' }: MetricRowProps) {
  const colorClass =
    accent === 'green'
      ? 'text-green-700'
      : accent === 'red'
        ? 'text-red-700'
        : 'text-gray-900'

  return (
    <div className="flex justify-between items-center py-2.5 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className={`text-sm font-semibold font-mono ${colorClass}`}>
        {value != null ? formatNumber(value) : '—'}
      </span>
    </div>
  )
}

export function BatchJobDetailPage() {
  const { executionId } = useParams<{ executionId: string }>()
  const navigate = useNavigate()

  const {
    data: execution,
    isLoading,
    error,
  } = useQuery<BatchJobExecution>({
    queryKey: ['batch-jobs', 'execution', executionId],
    queryFn: () => getBatchJobExecution(executionId!),
    enabled: !!executionId,
  })

  if (isLoading) {
    return (
      <div className="p-6">
        <LoadingState message="Loading execution details..." />
      </div>
    )
  }

  if (error || !execution) {
    return (
      <div className="p-6 space-y-4">
        <Button variant="ghost" onClick={() => navigate('/batch-jobs/history')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to History
        </Button>
        <ErrorState
          error={error as Error}
          message="Failed to load execution details"
          onRetry={() => navigate(0)}
        />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Back + Title */}
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/batch-jobs/history')} className="mb-3">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to History
        </Button>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900">{execution.displayName}</h1>
          <JobStatusBadge status={execution.status} />
        </div>
        <p className="text-xs text-gray-400 font-mono mt-1">{execution.id}</p>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Job Info */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h2 className="text-base font-semibold text-gray-900 mb-2">Execution Info</h2>
          <div>
            <InfoRow label="Job Name" value={execution.jobName} />
            <InfoRow
              label="Account ID"
              value={execution.accountId != null ? `Account ${execution.accountId}` : 'All Accounts'}
            />
            <InfoRow
              label="Trigger Type"
              value={<TriggerTypeBadge triggerType={execution.triggerType} />}
            />
            <InfoRow label="Triggered By" value={execution.triggeredBy || '—'} />
            <InfoRow label="Started At" value={formatDate(execution.startedAt)} />
            <InfoRow
              label="Completed At"
              value={execution.completedAt ? formatDate(execution.completedAt) : '—'}
            />
            <InfoRow label="Duration" value={formatDuration(execution.durationMs)} />
            <InfoRow label="Schedule" value={formatSchedule(execution.scheduleExpression)} />
          </div>
        </div>

        {/* Right: Metrics */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h2 className="text-base font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <BarChart2 className="h-4 w-4 text-indigo-600" />
            Metrics
          </h2>
          {execution.metrics ? (
            <div>
              <MetricRow label="Rows Processed" value={execution.metrics.rowsProcessed} />
              <MetricRow label="Rows Inserted" value={execution.metrics.rowsInserted} accent="green" />
              <MetricRow label="Rows Updated" value={execution.metrics.rowsUpdated} />
              <MetricRow label="Rows Failed" value={execution.metrics.rowsFailed} accent="red" />
              <MetricRow label="Batch Count" value={execution.metrics.batchCount} />
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-gray-400">
              No metrics available for this execution.
            </div>
          )}
        </div>
      </div>

      {/* Error box — shown only on FAILED */}
      {execution.status === 'FAILED' && execution.errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
          <h2 className="text-base font-semibold text-red-700 flex items-center gap-2 mb-3">
            <AlertCircle className="h-5 w-5" />
            Error Details
          </h2>
          <pre className="text-sm text-red-700 font-mono whitespace-pre-wrap break-all">
            {execution.errorMessage}
          </pre>
        </div>
      )}
    </div>
  )
}
